import { useEffect, useRef, useState } from 'react';
import roles from 'helpers/roles';
import { get, post } from 'helpers/apiHelpers';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import getReportFileNameWithMultiplier from 'helpers/getReportFileNameWithMultiplier';
import { isGranted } from 'helpers/helpers';
import ScrollButton from 'components/ScrollButton/ScrollButton';
import { debounce } from 'lodash';

import IndividualMultiplierReport from './IndividualMultiplierReport/IndividualMultiplierReport';
import MIME_TYPES from './constants/mimeTypes';
import { DOWNLOAD_REPORT_BUTTON_ID } from './IndividualMultiplierReport/constants/reportButtonId';

const IndividualMultiplierShoppingList = () => {
  const { user } = useSelector(state => state.Auth);
  const { t } = useTranslation();
  const containerRef = useRef(null);

  const [fakturowniaWarehouseModule, setFakturowniaWarehouseModule] = useState({
    enabled: false,
  });

  const [isScrollable, setIsScrollable] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const handleScroll = () => {
    const scrollTargetElement = document.getElementById(
      DOWNLOAD_REPORT_BUTTON_ID
    );

    const body = document.querySelector('body');

    const isBodyScroll = window.innerWidth <= 992;

    const { scrollTop } = body;
    const { clientHeight } = body;
    const targetOffset = scrollTargetElement?.offsetTop || 0;

    const isScrolledToBottom = isBodyScroll
      ? scrollTop + clientHeight >= targetOffset
      : window.innerHeight + window.scrollY >= targetOffset;

    setIsAtBottom(isScrolledToBottom);
  };

  const initShoppingList = async () => {
    const index = user.company.lastIndexOf('/') + 1;
    const companyId = user.company.slice(index, user.company.length);
    if (isGranted(roles.ROLE_EDIT_COMPANY)) {
      const { configuration } = await get(`/company/${companyId}/modules`);

      setFakturowniaWarehouseModule(configuration?.FakturowniaWarehouse);
    }
  };

  useEffect(() => {
    initShoppingList();

    const body = document.querySelector('body');
    const debouncedScroll = debounce(handleScroll, 200);

    body.addEventListener('scroll', debouncedScroll);

    window.addEventListener('scroll', debouncedScroll);

    const resizeObserver = new ResizeObserver(entries => {
      const [entry] = entries;
      const newHeight = entry.contentRect.height;

      setIsScrollable(newHeight > window.innerHeight);
      setIsAtBottom(false);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      body.removeEventListener('scroll', debouncedScroll);

      window.removeEventListener('scroll', debouncedScroll);
      debouncedScroll.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, []);

  const bookInWarehouse = params =>
    post('/fakturownia/warehouse/reservation', { params });

  return (
    <div ref={containerRef}>
      <IndividualMultiplierReport
        name="SHOPPING_LIST"
        title={t('reports.reportShoppingList')}
        url="/reports/shopping"
        previewXlsxAvailable={true}
        mimeTypes={[MIME_TYPES.xlsx]}
        role={roles.ROLE_SHOW_REPORT_SHOPPING}
        reportConfiguration={{
          report: 'Shopping',
          fields: [
            {
              size: 12,
              field: 'reportType',
              inputType: 'select',
              inputProps: {
                multiple: false,
                options: [
                  'WEIGHT_SUMMARY',
                  'QUANTITY_SUMMARY',
                  'ALL_SUMMARY',
                ].map(key => ({
                  value: key,
                  label: t(`reports.reportShoppingList.reportType.${key}`),
                })),
              },
            },
            {
              size: 12,
              field: 'roundDecimalPoints',
              inputType: 'number',
            },
          ],
        }}
        fileName={params =>
          getReportFileNameWithMultiplier(
            params,
            t('common.mainMenu.reportsShoppingList')
          )
        }
        useDateRange={true}
        useZoneCategories={true}
        useSubBrands={true}
        showMultiplier={true}
        shouldValidateBrands={false}
        validateDatePicker={true}
        usePost={true}
        useRecipeTags={true}
        singleSelectRecipeTags={true}
        isTagSelectV2Enabled={true}
        useWarehouse={fakturowniaWarehouseModule.enabled}
        singleSelectWarehouse={fakturowniaWarehouseModule.enabled}
        customActions={
          fakturowniaWarehouseModule.enabled
            ? [
                {
                  onClick: bookInWarehouse,
                  label: t('$*reports.shoppingList.bookInWarehose'),
                },
              ]
            : []
        }
        archiveReportNames={['SHOPPING_LIST']}
        isSalesMarketLangViewEnabled={true}
      />
      <ScrollButton isScrollable={isScrollable} isAtBottom={isAtBottom} />
    </div>
  );
};

export default IndividualMultiplierShoppingList;
